import React, { useState } from 'react'
import copy from 'copy-to-clipboard'
import { ExpandableRow } from 'transfix-ui/components/ExpandableRow'
import {
  ModalBase,
  IModalBaseProps,
  MODAL_TYPES,
  ConfirmationModal,
} from 'transfix-ui/components/Modals'
import { Box, Button, makeStyles, Typography } from 'transfix-ui/core'
import { CopyIcon, DeleteIcon } from 'transfix-icons'
import { brandColors, Card, IconButton, Spinner } from 'transfix-ui'
import { useModal } from 'utils/hooks'
import { runParserTests, updateParserTests, updateParserVersion, updateParserTest } from '../api'
import {
  ParserTest,
  ParserTestRequest,
  ParserTestResult,
  ParserVersion,
  parserVersionRequest,
} from '../types'
import { ParserTestConfirmationModal } from './ParserTestConfirmationModal'
import { ParserTestResultsModal } from './ParserTestResultsModal'

interface IParserTestsModalProps extends Omit<IModalBaseProps, 'modalType'> {
  parserTests: ParserTest[]
  parserVersion?: ParserVersion
  onLoadTest: (parserTest: ParserTest) => void
  onSuccess: () => void
  onClose: () => void
  ediInput: string | undefined
  expectedOutput: string | undefined
}

export type ParserTestConfirmationModal = 'parserTestConfirmationModal'
export type ParserTestResultsModal = 'parserTestResultsModal'
export type UpdateParserTestsModal = 'updateParserTestsModal'

const useParserTestModalStyles = makeStyles(theme => ({
  divider: {
    color: brandColors.black,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  button: {
    float: 'right',
    marginBottom: theme.spacing(1),
  },
  addParserBox: {
    height: '36px',
  },
  resultMessageBox: {
    height: '36px',
    alignItems: 'center',
    display: 'flex',
  },
  card: {
    marginBottom: theme.spacing(1),
  },
  loadButton: {
    marginBottom: theme.spacing(1),
  },
  updateTestsButton: {
    float: 'right',
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(1),
  },
  updateTestButton: {
    float: 'right',
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(1),
  },
}))

export const ParserTestsModal = ({
  ediInput,
  expectedOutput,
  onClose,
  onLoadTest,
  onSuccess,
  parserTests,
  parserVersion,
  ...props
}: IParserTestsModalProps) => {
  const actions: IModalBaseProps['actions'] = [
    {
      label: 'Close',
      action: () => {
        onClose()
      },
      type: 'dismiss',
      dataTest: 'close-button',
    },
  ]

  const {
    closeModal: closeParserTestConfirmationModal,
    isOpen: isParserTestConfirmationModalOpen,
    openModal: openParserTestConfirmationModal,
  } = useModal<ParserTestConfirmationModal>()

  const {
    closeModal: closeParserTestResultsModal,
    isOpen: isParserTestResultsModalOpen,
    openModal: openParserTestResultsModal,
  } = useModal<ParserTestResultsModal>()

  const { closeModal, isOpen, openModal } = useModal()

  const openParserTestConfirmModal = openParserTestConfirmationModal('parserTestConfirmationModal')

  const openTestResultsModal = openParserTestResultsModal('parserTestResultsModal')

  const classes = useParserTestModalStyles()

  const loadTestHandler = (parserTest: ParserTest) => {
    onLoadTest(parserTest)
  }

  const [selectedParserTest, setSelectedParserTest] = useState<ParserTest>()

  const [parserTestResults, setParserTestResults] = useState<ParserTestResult[]>([])

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [successMessage, setSuccessMessage] = useState<string>('')

  const [errorMessage, setErrorMessage] = useState<string>('')

  const runAllParserTests = async () => {
    if (parserVersion) {
      const parserTestResults: ParserTestResult[] = await runParserTests(parserVersion)
      setParserTestResults(parserTestResults)
    }
  }

  const runTestsHandler = async () => {
    const saveVersionBody: parserVersionRequest = {
      configurable_parser_version: {
        is_live: parserVersion?.is_live,
        parser_library: parserVersion?.parser_library,
        stupidedi_config: parserVersion?.stupidedi_config,
        edi_mapper_tfx12_definition_id: parserVersion?.edi_mapper_tfx12_definition_id,
        extraction_template: parserVersion?.extraction_template,
        conversion_instructions: parserVersion?.conversion_instructions,
        element_conversion: parserVersion?.element_conversion,
        document_conversion: parserVersion?.document_conversion,
        notes: parserVersion?.notes,
      },
    }
    if (!parserVersion?.is_live && !parserVersion?.was_ever_live) {
      await updateParserVersion(saveVersionBody, parserVersion)
    }
    await runAllParserTests()
    openTestResultsModal()
  }

  const updateTestsHandler = async () => {
    closeModal()
    const saveVersionBody: parserVersionRequest = {
      configurable_parser_version: {
        is_live: parserVersion?.is_live,
        parser_library: parserVersion?.parser_library,
        stupidedi_config: parserVersion?.stupidedi_config,
        edi_mapper_tfx12_definition_id: parserVersion?.edi_mapper_tfx12_definition_id,
        extraction_template: parserVersion?.extraction_template,
        conversion_instructions: parserVersion?.conversion_instructions,
        element_conversion: parserVersion?.element_conversion,
        document_conversion: parserVersion?.document_conversion,
        notes: parserVersion?.notes,
      },
    }
    if (!parserVersion?.is_live && !parserVersion?.was_ever_live) {
      await updateParserVersion(saveVersionBody, parserVersion)
    }
    setIsLoading(true)
    if (parserVersion) {
      try {
        const result = await updateParserTests(parserVersion)
        if (result?.errors) {
          setErrorMessage(result?.errors)
          setSuccessMessage('')
        } else {
          setErrorMessage('')
          setSuccessMessage('Tests Successfully Upated.')
        }
      } catch (error) {
        setErrorMessage(error.toString())
        setSuccessMessage('')
      }
    }
    onSuccess()
    setIsLoading(false)
  }

  const updateTestHandler = async (parserTest: ParserTest) => {
    const body: ParserTestRequest = {
      configurable_parser_test: {
        edi_mapper_configurable_parser_id: parserTest.edi_mapper_configurable_parser_id,
        name: parserTest.name,
        edi_input: ediInput || '',
        expected_output: JSON.stringify(expectedOutput),
        is_live: parserTest.is_live,
        notes: parserTest.notes || '',
      },
    }
    try {
      await updateParserTest(body, parserTest)
      setErrorMessage('')
      setSuccessMessage(`Parser Test ${parserTest.name} successfully upated.`)
    } catch (error) {
      setErrorMessage(error.toString())
      setSuccessMessage('')
    }
    onSuccess()
  }

  return (
    <ModalBase
      dataTest='ParserVersionModal'
      modalType={MODAL_TYPES.form}
      size={7}
      open
      actions={actions}
      onClose={onClose}
      closeOnBackdropClick
      {...props}>
      <Box paddingBottom='50px'>
        <ConfirmationModal
          title='Update all tests with current data?'
          size={3}
          open={isOpen('updateTestsModal')}
          onClose={closeModal}
          actions={[
            {
              label: 'Cancel',
              type: 'dismiss',
              action: closeModal,
            },
            {
              label: 'Confirm',
              type: 'confirm',
              action: updateTestsHandler,
            },
          ]}
        />
        <Typography variant='h3'>Parser Tests</Typography>
        <Box className={classes.addParserBox}>
          <Button onClick={openModal('updateTestsModal')} className={classes.updateTestsButton}>
            Update All Tests
          </Button>
          <Button onClick={runTestsHandler} className={classes.button}>
            Run All Tests
          </Button>
        </Box>
        <Box className={classes.resultMessageBox}>
          {successMessage && (
            <Typography variant='body1' data-test='success-message'>
              {successMessage}
            </Typography>
          )}
          {errorMessage && (
            <Typography variant='body1' data-test='failure-message'>
              {errorMessage}
            </Typography>
          )}
        </Box>
        {isLoading ? (
          <Spinner position='relative' />
        ) : (
          parserTests.map(parserTest => (
            <Box mt={2} key={`${parserTest.id}-${parserTest.name}`}>
              <ExpandableRow
                row={
                  <Box alignItems='center' display='flex'>
                    <Typography variant='h4'>
                      {parserTest.name} ( {parserTest.is_live ? 'Live' : 'Not Live'} ){' '}
                    </Typography>
                    <IconButton
                      Icon={DeleteIcon}
                      color='tertiary'
                      size='small'
                      onClick={e => {
                        e.stopPropagation()
                        setSelectedParserTest(parserTest)
                        openParserTestConfirmModal()
                      }}
                      label='Delete Test'
                    />
                  </Box>
                }
                rowClass='card'
                details={
                  <Box>
                    <Box>
                      <Button
                        className={classes.loadButton}
                        onClick={() => {
                          loadTestHandler(parserTest)
                        }}>
                        Load this test
                      </Button>
                      <Button
                        className={classes.updateTestButton}
                        onClick={() => {
                          updateTestHandler(parserTest)
                        }}>
                        Update test
                      </Button>
                      <Card
                        collapsible
                        collapsedByDefault
                        mini
                        title='Edi Input'
                        className={classes.card}>
                        <Box>
                          <Box className={classes.button} />
                          <IconButton
                            Icon={CopyIcon}
                            color='tertiary'
                            size='small'
                            onClick={() => copy(JSON.stringify(parserTest.edi_input))}
                            label='Copy EDI'
                          />
                          {JSON.stringify(parserTest.edi_input)}
                        </Box>
                      </Card>
                      <Card collapsible collapsedByDefault mini title='Expected Output'>
                        <Box>
                          <IconButton
                            Icon={CopyIcon}
                            color='tertiary'
                            size='small'
                            onClick={() => copy(JSON.stringify(parserTest.expected_output))}
                            label='Copy EDI'
                          />
                          {JSON.stringify(parserTest.expected_output)}
                        </Box>
                      </Card>
                    </Box>
                  </Box>
                }
              />
            </Box>
          ))
        )}
      </Box>
      {isParserTestConfirmationModalOpen('parserTestConfirmationModal') && (
        <ParserTestConfirmationModal
          onClose={closeParserTestConfirmationModal}
          selectedParserTest={selectedParserTest}
          onSuccess={onSuccess}
        />
      )}
      {isParserTestResultsModalOpen('parserTestResultsModal') && (
        <ParserTestResultsModal
          onClose={closeParserTestResultsModal}
          parserTestResults={parserTestResults}
        />
      )}
    </ModalBase>
  )
}
